import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoPipe } from '@jsverse/transloco';
import { MedicalComponentSearchDialogComponent } from 'guideline/feature-component-search';
import { produce } from 'immer';
import { map } from 'rxjs';
import { GuidelineService } from 'shared/domain';
import { ButtonDirective, DialogService, IconDirective } from 'shared/ui-common';
import { isTruthy, OrganisationSettingsService } from 'shared/util-common';

import {
  FlowchartActionDto,
  FlowchartElementType,
  GuidelineActionDto,
  GuidelineActionSignificance,
  SelectionBehavior,
  TimeSpanDto,
} from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { CustomisableFlowchartElementComponent } from '../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/customisable-flowchart-element/customisable-flowchart-element.component';
import { FlowchartNodeConfig } from '../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/flowchart-customisations-settings-tab.component';
import { DividerComponent } from '../../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { TitleDescriptionComponent } from '../../chapter-sidebar-wrapper/components/title-description/title-description.component';
import { GuidelineSidebarBasics, GuidelineSidebarBasicsComponent } from '../../guideline-sidebar-basics/guideline-sidebar-basics.component';
import { RecurrenceComponent } from '../../sub-components/recurrence/recurrence.component';
import { SelectionBehaviorComponent } from '../../sub-components/selection-behavior/selection-behavior.component';
import { SidebarMedicationsComponent } from '../../sub-components/sidebar-medications/sidebar-medications.component';
import { SignificancePreferenceComponent } from '../../sub-components/significance-preference/significance-preference.component';
import { TechnicalInfoComponent } from '../../sub-components/technical-info/technical-info.component';

@Component({
  standalone: true,
  imports: [
    MatTabsModule,

    SidebarMedicationsComponent,
    TranslocoPipe,
    TitleDescriptionComponent,
    SelectionBehaviorComponent,
    SignificancePreferenceComponent,
    RecurrenceComponent,
    TechnicalInfoComponent,
    MatIconModule,
    MatIconModule,
    IconDirective,
    GuidelineSidebarBasicsComponent,
    DividerComponent,
    ButtonDirective,
    CustomisableFlowchartElementComponent,
  ],
  selector: 'flowchart-sidebar-container',
  templateUrl: './flowchart-sidebar-container.component.html',
  styleUrls: ['./flowchart-sidebar-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowchartSidebarContainerComponent {
  private dialogService = inject(DialogService);
  private organisationSettingsService = inject(OrganisationSettingsService);
  private guidelineService = inject(GuidelineService);

  public sidebarElement = input<FlowchartActionDto | undefined>(undefined);

  @Input() public taxonomyId: string;

  @Output() public flowchartElementDataChanged = new EventEmitter<Partial<FlowchartActionDto>>();

  protected flowchartElementType = FlowchartElementType;

  protected component = new FormControl();
  protected showAllMedications = false;
  protected resetMedicalComponent = false;
  protected organisationSettings = computed(
    () => this.guidelineService.guidelineData()?.organisation?.organisationSettings ?? this.organisationSettingsService.settings(),
  );

  protected colorPresets = computed(() => this.organisationSettings().customisations?.colorPresets ?? []);

  protected hasColorPresets = computed(() => {
    return this.colorPresets().length > 0;
  });

  protected flowchartNodeConfig = computed<FlowchartNodeConfig>(() => {
    const colorPresets = this.colorPresets();
    const organisationCustomisations = this.organisationSettings().customisations?.flowchart;
    const elementColorPreset = this.sidebarElement().colorPresetId;

    const colorPreset = colorPresets.find((preset) => preset.id === (elementColorPreset ?? organisationCustomisations?.container));

    return {
      type: 'container',
      content: 'Container',
      colorPreset,
    };
  });

  protected onTitleChanged(title: string) {
    this.flowchartElementDataChanged.emit({ guidelineAction: { title } });
  }

  protected onDescriptionChanged(description: string) {
    this.flowchartElementDataChanged.emit({ guidelineAction: { description } });
  }

  protected onShowDescriptionChange(showDescription: boolean): void {
    this.flowchartElementDataChanged.emit({ showDescription });
  }

  protected onColorPresetChanged(colorPresetId: string): void {
    this.flowchartElementDataChanged.emit({ colorPresetId });
  }

  protected onRecurrenceChanged($event: number) {
    this.flowchartElementDataChanged.emit({
      ...this.sidebarElement(),
      guidelineAction: { ...this.sidebarElement().guidelineAction, recurrence: $event },
    });
  }

  protected onRecurrenceIntervalChanged($event: TimeSpanDto) {
    this.flowchartElementDataChanged.emit({
      ...this.sidebarElement(),
      guidelineAction: { ...this.sidebarElement().guidelineAction, recurrenceInterval: $event },
    });
  }

  protected onMedicationsChanged($event: GuidelineActionDto[]) {
    this.flowchartElementDataChanged.emit({ guidelineAction: { medicationRequests: $event } });
  }

  protected onSelectionBehaviourChanged(selectionBehavior: SelectionBehavior) {
    this.flowchartElementDataChanged.emit({ guidelineAction: { selectionBehaviorForChildren: selectionBehavior } });
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    const newComponent = produce({ ...this.sidebarElement() }, (draft) => {
      draft.references = basics.references;
      draft.links = basics.links;
      draft.attachments = basics.attachments;
    });

    this.flowchartElementDataChanged.emit(newComponent);
  }

  protected onSignificancePreferenceChanged(
    $event: Partial<{
      significance: GuidelineActionSignificance;
      preferredOptionOrder: number;
    }>,
  ) {
    const updateObject = {
      ...($event.significance !== undefined && { significance: $event.significance }),
      ...($event.preferredOptionOrder !== undefined && { preferredOptionOrder: $event.preferredOptionOrder }),
    };

    this.flowchartElementDataChanged.emit({ guidelineAction: { ...updateObject } });
  }

  protected openSearchDialog() {
    const searchDialog = this.dialogService.open(MedicalComponentSearchDialogComponent, {
      title: 'AddComponent',
      data: {
        definition: this.component,
        findingFilterActive: false,
        activityFilterActive: true,
        observationFilterActive: false,
        enableMultiSearch: true,
        elementType: this.sidebarElement().elementType,
        isFilterSearch: false,
        taxonomyId: this.taxonomyId,
      },
      disableClose: true,
    });

    searchDialog
      .afterClosed()
      .pipe(
        map((result) => result.value),
        isTruthy(),
      )
      .subscribe((result) => {
        this.flowchartElementDataChanged.emit({
          guidelineAction: {
            activityDefinition: result,
            activityDefinitionVersion: result.version,
            activityDefinitionCode: result.code,
            title: result.title,
          },
        });
      });
  }

  protected removeAction() {
    this.flowchartElementDataChanged.emit({
      guidelineAction: {
        activityDefinition: null,
        activityDefinitionVersion: null,
        activityDefinitionCode: null,
        title: '',
      },
    });

    this.resetMedicalComponent = true;
  }

  protected toggleShowAllMedications(event): void {
    this.showAllMedications = event;
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
}
